import React, { useState } from 'react';
import { X, Loader2, AlertTriangle, ShieldAlert, ArrowRight, CheckCircle2 } from 'lucide-react';
import type { AdminUserListItem } from '@nova-mine/shared';
import { formatCoinAmount } from '@nova-mine/shared';

interface BalanceAdjustModalProps {
  user: AdminUserListItem | null;
  onClose: () => void;
  onSubmit: (userId: string, amount: number, reason: string) => Promise<void>;
}

export const BalanceAdjustModal: React.FC<BalanceAdjustModalProps> = ({
  user,
  onClose,
  onSubmit,
}) => {
  const [amountStr, setAmountStr] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const currentBalance = user.balance;
  const parsedAmount = parseInt(amountStr, 10);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount !== 0;
  const projectedBalance = isValidAmount ? currentBalance + parsedAmount : currentBalance;
  const isNegativeProjected = projectedBalance < 0;
  const isReasonValid = reason.trim().length >= 5;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidAmount) {
      setError('Please provide a valid non-zero integer coin adjustment amount.');
      return;
    }
    if (isNegativeProjected) {
      setError(`Adjustment would cause a negative balance (${projectedBalance.toLocaleString()} NOVA). Operation prohibited.`);
      return;
    }
    if (!isReasonValid) {
      setError('A mandatory reason of at least 5 characters is required for the immutable audit log.');
      return;
    }
    if (!confirmed) {
      setError('You must check the confirmation box before applying this adjustment.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await onSubmit(user.id, parsedAmount, reason.trim());
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Adjustment failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="w-full max-w-lg bg-space-900 border border-purple-500/20 rounded-3xl p-6 md:p-8 shadow-2xl relative shadow-purple-950/50">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-xl bg-space-800 text-gray-400 hover:text-white hover:bg-space-700 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-black text-white">Manual Balance Adjustment</h3>
            <p className="text-xs text-gray-400">
              User: <span className="text-white font-semibold">{user.first_name}</span> (Telegram ID: <span className="font-mono text-cyan-400">{user.telegram_id}</span>)
            </p>
          </div>
        </div>

        {/* Balance Preview Card */}
        <div className="mt-5 p-4 rounded-2xl bg-space-950 border border-white/5 space-y-3">
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-400">Current Balance:</span>
            <span className="font-mono font-bold text-white">{formatCoinAmount(currentBalance, 2)} NOVA</span>
          </div>

          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-400">Adjustment Delta:</span>
            <span className={`font-mono font-bold ${
              !isValidAmount
                ? 'text-gray-500'
                : parsedAmount > 0
                ? 'text-emerald-400'
                : 'text-rose-400'
            }`}>
              {isValidAmount ? (parsedAmount > 0 ? `+${parsedAmount.toLocaleString()}` : parsedAmount.toLocaleString()) : '0'} NOVA
            </span>
          </div>

          <div className="pt-2 border-t border-white/10 flex justify-between items-center text-xs">
            <span className="text-gray-300 font-semibold flex items-center gap-1.5">
              <span>Projected Balance:</span>
              <ArrowRight className="w-3.5 h-3.5 text-gray-500" />
            </span>
            <span className={`font-mono font-bold text-sm ${
              isNegativeProjected ? 'text-rose-400 underline decoration-rose-500' : 'text-nova-gold'
            }`}>
              {formatCoinAmount(projectedBalance, 2)} NOVA
            </span>
          </div>

          {isNegativeProjected && (
            <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-center space-x-2 text-rose-400 text-xs">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Deduction exceeds user balance. Negative balance is forbidden.</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-300 block mb-1">
              Adjustment Amount (Coins)
            </label>
            <div className="relative">
              <input
                type="number"
                step="1"
                placeholder="e.g. 50000 to grant or -10000 to deduct"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                required
                className="w-full bg-space-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm font-mono text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40"
              />
              <span className="absolute right-3.5 top-3 text-xs font-bold text-gray-500">NOVA</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Use positive integers to credit coins (e.g. 25000) or negative to deduct (e.g. -5000).
            </p>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs font-bold text-gray-300">
                Audited Reason / Justification
              </label>
              <span className={`text-[10px] ${reason.trim().length >= 5 ? 'text-emerald-400' : 'text-gray-500'}`}>
                {reason.trim().length}/5 min chars
              </span>
            </div>
            <textarea
              rows={2}
              placeholder="e.g. Dispute resolution ticket #4401 or VIP promo bonus"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="w-full bg-space-950 border border-white/10 rounded-xl px-3.5 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40 resize-none"
            />
          </div>

          {/* Audit Confirmation Checkbox */}
          <label className="flex items-start space-x-3 p-3 rounded-xl bg-purple-950/20 border border-purple-500/20 cursor-pointer hover:bg-purple-950/30 transition-colors">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 rounded border-purple-500/40 text-purple-600 focus:ring-purple-500 bg-space-950"
            />
            <span className="text-[11px] text-gray-300 leading-snug">
              I certify this manual balance adjustment is legitimate and confirm that an immutable transaction row will be permanently written to the ledger with my administrator signature.
            </span>
          </label>

          {error && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl bg-space-800 hover:bg-space-700 text-gray-300 text-xs font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !isValidAmount || !isReasonValid || !confirmed || isNegativeProjected}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-purple-600 to-indigo-600 hover:from-amber-400 hover:to-indigo-500 text-white text-xs font-bold flex items-center space-x-2 shadow-lg shadow-purple-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              <span>Commit Ledger Adjustment</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
